/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
*/
import React, {ReactNode} from 'react';
import Grid, {GridSize} from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {BrakeChamberPosition, BrakeTypeProps, VehicleWithTrailerTires} from '@ion/core/src/types/entities';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';

export interface SensorListItemProps {
  something?: string;
  columnContent: ReactNode[] | BrakeTypeProps[];
  header?: boolean;
  open?: boolean;
  setSelectedTire?: (val: VehicleWithTrailerTires) => void;
  wheelLocation?: VehicleWithTrailerTires;
  itemId?: string | BrakeChamberPosition;
  onItemHover?: Function;
  // current theme only - the last column holds just an icon (e.g. a row's
  // expand arrow), so it takes only that icon's width and the other columns
  // share the rest of the row ...
  trailingIconColumn?: boolean;
}

// the current theme's trailing icon column - its icon's own width ...
const TRAILING_ICON_COLUMN_WIDTH = 24;

// the current theme's column widths with a trailing icon column - the other
// columns share the row equally ...
const FILL_COLUMN_STYLE          = {flex: '1 1 0', maxWidth: 'none'};
const TRAILING_ICON_COLUMN_STYLE = {flex: `0 0 ${TRAILING_ICON_COLUMN_WIDTH}px`, maxWidth: 'none'};

const useStyles = makeStyles((theme: DLSTheme) => {
  const contentStyles = getAssetDetailsContentStyles();
  return {
    sensorListItem : {
      fontWeight    : ({header}: {header: boolean}) => header ? 'bold' : 'normal',
      textTransform : ({header}: {header: boolean}) => header ? 'uppercase' : 'none',
      width         : '100%',
      whiteSpace    : 'nowrap'
    },
    sensorListColumn1 : {
      fontWeight     : 700,
      fontSize       : '14px',
      color          : ({header}: {header: boolean}) => header ? dlsColor('black') : dlsColor('grey3'),
      letterSpacing  : '1px',
      textTransform  : 'uppercase',
      justifyContent : 'flex-start !important'

    },
    sensorListColumn : {
      ...dlsAlt('typography', 'label1'),
      display        : 'flex',
      alignSelf      : 'center',
      justifyContent : 'center'

    },
    caution : {
      color : dlsAlt('maps', 'colors', 'lightOrange')
    },
    warning : {
      color : dlsColor('caRed')
    },
    // current theme only - the first (name/label) column reads as the
    // shared uppercase field label, every other column as the shared value
    // text, matching the Asset Details Drawer's other designed sections ...
    currentSensorListColumn1 : {
      ...contentStyles.label,
      justifyContent : 'flex-start !important'
    },
    // a header label over a centered value column stays centered over it ...
    currentHeaderColumn : {
      justifyContent : 'center !important'
    },
    currentSensorListColumn : {
      ...contentStyles.valueText,
      display        : 'flex',
      alignSelf      : 'center',
      justifyContent : 'center'
    }
  };
});

/**
 * Each item in the sensor list. This gets passed as a clickable
 * expansion header to the expandable list, but stands alone as a
 * column list item grid. Each column can render a string or a
 * component. Header items are given different styling per the DLS.
 *
 * Must be provided no more than 6 columns and (preferably)
 * a length that divides evenly into 12. In the current theme, a last column
 * holding just an icon (`trailingIconColumn`) takes only that icon's width,
 * the other columns sharing the full row.
 *
 * ```typescript
 *   import {SensorListItem} from '@ion/features/src/smarterTrailer';
 *
 *   <SensorListItem header
 *     columnContent={[
 *       'Name'
 *       <LastName />,
 *       <Address zip={zip} />
 *     ]} />
 * />
 * ```
 */
export const SensorListItem = ({
  itemId,
  columnContent,
  onItemHover,
  header = false,
  trailingIconColumn = false
}: SensorListItemProps) => {
  const classes             = useStyles({header});
  const {isCurrent}         = useThemeContext();
  const columnContentLength = columnContent.length;
  const fitsTrailingIcon    = isCurrent && trailingIconColumn;
  // the current theme's header reads as field labels throughout ...
  const getCurrentColumnClass = (i: number): string => (
    !i || header ? classes.currentSensorListColumn1 : classes.currentSensorListColumn
  );
  const getColumnStyle = (i: number) => {
    let style;
    if (fitsTrailingIcon) {
      style = i === columnContentLength - 1 ? TRAILING_ICON_COLUMN_STYLE : FILL_COLUMN_STYLE;
    } else if (!isCurrent) {
      style = {
        fontWeight : !header && i !== 0 ? 400 : 700,
        color      : header && dlsColor('black')
      };
    }
    return style;
  };

  if (!columnContentLength) {
    throw new Error(`${SensorListItem.displayName} - must provide at least 1 column`);
  };
  if (columnContentLength > 6) {
    throw new Error(`${SensorListItem.displayName} - cant have more than 6 columns`);
  };

  return (
    <div onMouseOver={function() { onItemHover?.(itemId); }}
      onMouseLeave={function() { onItemHover?.(null); }}
      className={classes.sensorListItem}>
      <Grid container spacing={2}>
        {
          columnContent.map((column, i) => (
            <Grid item xs={12 / columnContentLength as GridSize}
              key={`sensor-list-item-${i}`}
              style={getColumnStyle(i)}
              className={classnames(
                classes[column?.status],
                classes.sensorListColumn,
                !i && classes.sensorListColumn1,
                isCurrent && getCurrentColumnClass(i),
                isCurrent && header && i && classes.currentHeaderColumn
              )}>
              {column?.value || column}
            </Grid>
          ))
        }
      </Grid>
    </div>
  );
};

SensorListItem.displayName = 'SensorListItem';

export default SensorListItem;
